import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { passwordResetService } from '../../services/supabaseService';
import './LoginNew.css';

const JudgePasswordReset = ({ onBack, onResetSuccess }) => {
  const [step, setStep] = useState('email'); // 'email' | 'token' | 'success' | 'error'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!email.trim()) {
      setError('Por favor ingresa tu correo electrónico');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un correo válido');
      setLoading(false);
      return;
    }

    try {
      const result = await passwordResetService.requestReset(email);
      
      if (result.error) {
        setMessage(result.error); // Mostrar mensaje genérico por seguridad
        return;
      }

      // En producción, esto se enviaría por email
      // Por ahora, mostrar el token para desarrollo
      setToken(result.data.resetToken);
      setStep('token');
      setMessage('Se ha generado un código de reset. Por favor ingrésalo a continuación.');
    } catch (err) {
      setError('Error al solicitar reset de contraseña');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!token.trim()) {
      setError('Por favor ingresa el código de reset');
      setLoading(false);
      return;
    }

    if (!newPassword) {
      setError('Por favor ingresa la nueva contraseña');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      // En producción, esta contraseña se encriptaría
      const result = await passwordResetService.resetPassword(
        token,
        newPassword // En producción: hashPassword(newPassword)
      );

      if (result.error) {
        setError(result.error);
        return;
      }

      setStep('success');
      setTimeout(() => {
        if (onResetSuccess) onResetSuccess();
        onBack();
      }, 2000);
    } catch (err) {
      setError('Error al resetear contraseña');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <button
          onClick={onBack}
          className="back-button"
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: '#f4f4f5',
            border: '1px solid #e4e4e7',
            borderRadius: 10,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#71717a'
          }}
        >
          <ArrowLeft size={20} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" style={{ maxWidth: 200, width: '100%', height: 'auto', objectFit: 'contain', marginBottom: 12 }} />
        </div>

        {step === 'email' && (
          <>
            <h2 style={{ marginBottom: '8px', color: '#111827' }}>Recuperar Contraseña</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.9rem' }}>
              Ingresa tu correo electrónico para recibir un código de reset
            </p>

            <form onSubmit={handleRequestReset}>
              <div className="form-group">
                <label>Correo Electrónico</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu.email@ejemplo.com"
                  disabled={loading}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  display: 'flex',
                  gap: '8px',
                  color: '#991b1b',
                  marginBottom: '16px'
                }}>
                  <AlertCircle size={18} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9rem' }}>{error}</span>
                </div>
              )}

              {message && (
                <div style={{
                  padding: '12px',
                  background: '#d1fae5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '6px',
                  color: '#065f46',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => !loading && (e.target.style.background = '#7c3aed')}
                onMouseLeave={(e) => !loading && (e.target.style.background = '#8b5cf6')}
              >
                {loading ? 'Enviando...' : 'Enviar Código'}
              </button>
            </form>
          </>
        )}

        {step === 'token' && (
          <>
            <h2 style={{ marginBottom: '8px', color: '#111827' }}>Ingresa el Código</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.9rem' }}>
              Se te envió un código al correo. Ingrésalo a continuación
            </p>

            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>Código de Reset</label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value.toUpperCase())}
                  placeholder="ABC12345"
                  maxLength="20"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Confirmar Contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  disabled={loading}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  display: 'flex',
                  gap: '8px',
                  color: '#991b1b',
                  marginBottom: '16px'
                }}>
                  <AlertCircle size={18} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9rem' }}>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => !loading && (e.target.style.background = '#7c3aed')}
                onMouseLeave={(e) => !loading && (e.target.style.background = '#8b5cf6')}
              >
                {loading ? 'Procesando...' : 'Cambiar Contraseña'}
              </button>
            </form>
          </>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '16px' }} />
            <h2 style={{ color: '#065f46', marginBottom: '8px' }}>¡Éxito!</h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Tu contraseña ha sido actualizada correctamente
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
              Redirigiendo al login...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JudgePasswordReset;
